
import React, { useMemo, useState, useEffect } from 'react';
import { useStore } from '../../hooks/useStore.ts';
import { api } from '../../services/api.ts';
import { calculateFinancialMetrics, formatCurrency } from '../../services/financialService.ts';
import { MetricCard } from '../MetricCard.tsx';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Project, FinancialMetrics, EVMSnapshot } from '../../types.ts';
import { TrendingUp, Activity, Plus, Loader2, DollarSign, Cpu, ShieldCheck } from 'lucide-react';
import { Modal } from '../Modal.tsx';


export const FinancialsSection: React.FC<{ project: Project }> = ({ project }) => {
  const rawFin = useStore(() => api.evm.list(f => f.projectId === project.id), [project.id], 'evm');
  const changeOrders = useStore(() => api.changeOrders.list(co => co.projectId === project.id), [project.id], 'changeOrders') || [];
  
  const [adding, setAdding] = useState(false);
  const [brief, setBrief] = useState<string>('');
  const [loadingBrief, setLoadingBrief] = useState(false);

  const [newSnapshot, setNewSnapshot] = useState<Partial<EVMSnapshot>>({
    date: new Date().toISOString().split('T')[0],
    plannedValue: 0,
    earnedValue: 0,
    actualCost: 0,
    periodLabel: ''
  });
  
  const fins: FinancialMetrics[] = useMemo(() => {
    if (!rawFin) return [];
    return rawFin
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .map(s => calculateFinancialMetrics(s, project.contractAmount, changeOrders));
  }, [project, rawFin, changeOrders]);

  const lastFin = fins[fins.length - 1];

  useEffect(() => {
    if (lastFin) {
      generateBrief();
    }
  }, [lastFin]);

  const generateBrief = async () => {
    setLoadingBrief(true);
    try {
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Commercial PM Context: Contract=$${project.contractAmount}, Spent=$${lastFin.actualSpend}, Earned=$${lastFin.earnedValue}. Provide a blunt assessment of project profitability (Margin Index) and specifically mention any detected scope gap or unbilled exposure.`,
      });
      setBrief(response.text || '');
    } catch (e) {
      setBrief('Tracking within expected performance baseline.');
    } finally {
      setLoadingBrief(false);
    }
  };

  const saveSnapshot = async () => {
     if (!newSnapshot.date || project.id === undefined) return;
     await api.evm.add({
         projectId: project.id,
         date: newSnapshot.date!,
         plannedValue: newSnapshot.plannedValue || 0,
         earnedValue: newSnapshot.earnedValue || 0,
         actualCost: newSnapshot.actualCost || 0,
         periodLabel: newSnapshot.periodLabel
     });
     setAdding(false);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black text-white p-8 rounded-[2.5rem] shadow-2xl border border-zinc-800 animate-in zoom-in-95 duration-200">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-6">{label}</p>
          <div className="space-y-4">
            <div className="flex justify-between gap-12 items-center">
              <span className="text-xs font-black uppercase tracking-widest text-zinc-400">Burned (AC)</span>
              <span className="text-lg font-black text-white">{formatCurrency(payload[0].value)}</span>
            </div>
            <div className="flex justify-between gap-12 items-center">
              <span className="text-xs font-black uppercase tracking-widest text-zinc-600">Baseline PV</span>
              <span className="text-lg font-black text-zinc-600">{formatCurrency(payload[1].value)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-12 animate-in fade-in duration-1000">
      <div className="grid lg:grid-cols-4 gap-12">
        <div className="lg:col-span-3 bg-white rounded-[3.5rem] border border-zinc-200 shadow-sm overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-700">
            <div className="px-12 pt-12 pb-8 flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="p-3 bg-black rounded-2xl text-white shadow-xl"><TrendingUp size={24}/></div>
                    <h3 className="text-3xl font-black text-black tracking-tight uppercase">Margin Performance</h3>
                  </div>
                  <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest">Cumulative Cost vs. Earned Baseline</p>
                </div>
                <button onClick={() => setAdding(true)} className="px-8 py-4 bg-black text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all flex items-center gap-3 shadow-xl">
                  <Plus size={16}/> Register Log
                </button>
            </div>

            <div className="flex-1 px-8 pb-12">
              <div className="h-[500px] w-full bg-zinc-50 rounded-[3rem] p-10 border border-zinc-100 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={fins} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#000000" stopOpacity={0.2}/> 
                        <stop offset="95%" stopColor="#000000" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="#e4e4e7" strokeOpacity={0.5} />
                    <XAxis dataKey="date" stroke="#d4d4d8" fontSize={10} fontWeight={900} tickLine={false} axisLine={false} dy={15} tickFormatter={(str) => new Date(str).toLocaleDateString(undefined, { month: 'short' }).toUpperCase()} />
                    <YAxis stroke="#d4d4d8" fontSize={10} fontWeight={900} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                    <Tooltip content={<CustomTooltip />} cursor={{stroke: '#e4e4e7', strokeWidth: 1}} />
                    <Area type="monotone" dataKey="actualSpend" stroke="#000000" fillOpacity={1} fill="url(#colorActual)" strokeWidth={5} name="Actual Spent" activeDot={{ r: 10, fill: '#000', stroke: '#fff', strokeWidth: 5 }} />
                    <Area type="monotone" dataKey="projectedSpend" stroke="#d4d4d8" strokeDasharray="10 10" fill="transparent" name="Projection" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
        </div>
        <div className="space-y-12">
            <MetricCard label="Margin Remaining" value={lastFin ? formatCurrency(lastFin.remainingBudget) : 'N/A'} variant="dark" />
            <MetricCard label="Unbilled Exposure" value={lastFin ? formatCurrency(lastFin.variance) : '0'} status={lastFin?.variance < 0 ? 'danger' : 'success'} subValue={lastFin?.variance < 0 ? 'Work performed exceeds contract coverage' : 'Fully covered by contract/COs'} />
        </div>
      </div>

      {/* ZEN REFACTORED FORENSIC BRIEF */}
      <div className="bg-white rounded-[3rem] border border-zinc-200 p-12 shadow-sm relative overflow-hidden group">
         <div className="space-y-10">
            {/* TOP ROW: HORIZONTAL METRIC BOXES */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
               <div className="flex items-center gap-4">
                  <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-100 text-black shadow-inner group-hover:scale-110 transition-transform">
                     <ShieldCheck size={24} className="text-emerald-500"/>
                  </div>
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-zinc-400">Intelligence Synthesis</h4>
                    <div className="text-xl font-black text-black uppercase tracking-tight mt-1">Forensic Brief</div>
                  </div>
               </div>
               
               <div className="flex flex-wrap gap-4">
                  <div className="px-6 py-3 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center gap-4">
                    <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest">Confidence Index</span>
                    <span className="text-sm font-black text-emerald-600">98.4%</span>
                  </div>
                  <div className="px-6 py-3 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                       <Cpu size={12} className="text-zinc-300"/>
                       <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest">Node Path</span>
                    </div>
                    <span className="text-sm font-black text-black tracking-widest uppercase">Engine-03</span>
                  </div>
                  {loadingBrief && (
                     <div className="px-6 py-3 flex items-center gap-3">
                        <Loader2 size={16} className="animate-spin text-zinc-300" />
                        <span className="text-[8px] font-black uppercase text-zinc-300 tracking-widest">Synthesizing...</span>
                     </div>
                  )}
               </div>
            </div>
            
            {/* BOTTOM ROW: CLEAN PERFORMANCE NARRATIVE */}
            <div className="pt-10 border-t border-zinc-50">
               <div className="flex justify-between items-center mb-6">
                  <span className="text-[9px] font-black uppercase text-zinc-300 tracking-[0.2em]">Operational Performance Narrative</span>
               </div>
               <div className="min-h-[100px]">
                  <p className="text-xl font-medium text-zinc-700 leading-[1.6] italic max-w-5xl">
                    {brief || "Awaiting synchronization of forensic cost indicators from WBS and site log data pools..."}
                  </p>
               </div>
            </div>
         </div>
      </div>

      {adding && (
        <Modal isOpen={adding} onClose={() => setAdding(false)} title="Operational Period Log" maxWidth="sm">
            <div className="space-y-8">
                <div>
                   <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-3">Sync Date</label>
                   <input type="date" className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-5 font-black focus:bg-white outline-none" value={newSnapshot.date} onChange={e => setNewSnapshot({...newSnapshot, date: e.target.value})} />
                </div>
                <div className="grid grid-cols-1 gap-8">
                   <div>
                      <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-3">Target Magnitude (PV)</label>
                      <input type="number" className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-5 font-black focus:bg-white outline-none" value={newSnapshot.plannedValue} onChange={e => setNewSnapshot({...newSnapshot, plannedValue: +e.target.value})} />
                   </div>
                   <div>
                      <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-3">Work Completed (EV)</label>
                      <input type="number" className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-5 font-black focus:bg-white outline-none" value={newSnapshot.earnedValue} onChange={e => setNewSnapshot({...newSnapshot, earnedValue: +e.target.value})} />
                   </div>
                   <div>
                      <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-3">Cost to Date (AC)</label>
                      <input type="number" className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-5 font-black focus:bg-white outline-none" value={newSnapshot.actualCost} onChange={e => setNewSnapshot({...newSnapshot, actualCost: +e.target.value})} />
                   </div>
                </div>
                <button onClick={saveSnapshot} className="w-full bg-black text-white py-6 rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] hover:scale-[1.01] shadow-2xl transition-all">Submit Periodic Update</button>
            </div>
        </Modal>
      )}
    </div>
  );
};
